<script>
    /**
     * @typedef paginator
     * @property {String} first_page_url
     * @property {String} last_page_url
     * @property {String} prev_page_url
     * @property {String} next_page_url
     * @property {Number} from
     * @property {Number} to
     * @property {Number} total
     * @property {Number} last_page
     * @property {String} path
     * @property {Number} per_page
     * @property {Number} current_page
     */
    export default {
        name: 'Paginator',

        props: {
            offset: {
                type: Number,
                required: false,
                default: 3,
            },
            paginator: {
                required: true,
                type: Object,
            },
        },

        computed: {
            disabledButton() {
                return 'items-center px-4 py-2 text-sm font-medium text-grey-dark bg-white border border-grey cursor-default leading-5 rounded-md cursor-not-allowed';
            },
            activeButton() {
                return 'items-center px-4 py-2 text-sm font-medium text-grey-darkest bg-white border border-grey-dark leading-5 rounded-md cursor-pointer hover:text-grey-darker hover:bg-grey-lighter focus:outline-none focus:ring-blue focus:border-blue-light transition ease-in-out duration-150';
            },
            // Attribute driven properties
            total() {
                return this.paginator.total;
            },
            perPage() {
                return this.paginator.per_page;
            },
            firstPage() {
                return 1;
            },
            lastPage() {
                return this.paginator.last_page;
            },
            currentPage() {
                return this.paginator.current_page;
            },
            fromElement() {
                return this.paginator.from;
            },
            toElement() {
                return this.paginator.to;
            },

            // Computation-driven properties
            pages() {
                let pages = [];

                let from = this.currentPage - Math.floor(this.offset / 2) - 2;
                if (from < this.firstPage) {
                    from = this.firstPage;
                }

                let to = this.currentPage + Math.floor(this.offset / 2) + 2;
                if (to > this.lastPage) {
                    to = this.lastPage;
                }

                for (let index = from; index <= to; index++) {
                    pages.push(index);
                }

                return pages;
            },
            hasPages() {
                return this.hasPaginator && (this.currentPage != 1 || this.hasMorePages);
            },
            hasMorePages() {
                return this.currentPage < this.lastPage;
            },
            onFirstPage() {
                return this.currentPage <= this.firstPage;
            },
            onLastPage() {
                return this.currentPage >= this.lastPage;
            },
            hasPaginator() {
                return Object.keys(this.paginator).length > 0;
            },
        },

        methods: {
            isValidPageNumber(page) {
                return page >= this.firstPage && page <= this.lastPage;
            },
            isCurrentPage(page) {
                return this.currentPage == page;
            },
            inRange(page) {
                return Math.abs(page - this.currentPage) < this.offset;
            },
            inDotRange(page) {
                return Math.abs(page - this.currentPage) == this.offset;
            },
            toPage(page) {
                if (this.isValidPageNumber(page)) {
                    this.$emit('input', page);
                }
            },
        },
    };
</script>

<template>
    <nav v-if="hasPaginator" aria-label="Pagination Navigation"
         class="flex flex-row flex-wrap justify-center md:justify-between align-items-center pt-4">
        <span class="self-center">
            {{ total === 0 ? '' : `Showing ${fromElement} to ${toElement} of ${total} entries` }}
        </span>

        <div v-if="hasPages" class="flex flex-row justify-between self-center">
            <!-- First Page -->
            <button v-if="onFirstPage"
                    aria-label="Already On First Page"
                    :class="disabledButton">
                <i class="fa fa-angle-double-left"></i>
            </button>
            <button v-else @click.stop="toPage(firstPage)"
                    aria-label="First Page"
                    :class="activeButton">
                <i class="fa fa-angle-double-left"></i>
            </button>

            <!-- Previous Page -->
            <button v-if="onFirstPage"
                    aria-label="No Previous Page"
                    :class="disabledButton">
                <i class="fa fa-angle-left"></i>
            </button>
            <button v-else @click.stop="toPage(currentPage - 1)"
                    aria-label="Previous Page"
                    :class="activeButton">
                <i class="fa fa-angle-left"></i>
            </button>

            <!-- Paginated Links -->
            <button v-for="index in pages"
                    v-if="inRange(index)" @click.stop="toPage(index)"
                    :class="`${activeButton} ${isCurrentPage(index) ? 'text-grey-darker bg-grey-lighter': ''}`">
                {{ index }}
            </button>
            <button v-else-if="inDotRange(index)" class="hidden sm:block" :class="disabledButton">
                ...
            </button>

            <!-- Next Page -->
            <button v-if="onLastPage"
                    aria-label="No Next Page"
                    :class="disabledButton">
                <i class="fa fa-angle-right"></i>
            </button>
            <button v-else @click.stop="toPage(currentPage + 1)"
                    aria-label="Next Page"
                    :class="activeButton">
                <i class="fa fa-angle-right"></i>
            </button>

            <!-- Last Page -->
            <button v-if="onLastPage"
                    aria-label="On Last Page"
                    :class="disabledButton">
                <i class="fa fa-angle-double-right"></i>
            </button>
            <button v-else @click.stop="toPage(lastPage)"
                    aria-label="Last Page"
                    :class="activeButton">
                <i class="fa fa-angle-double-right"></i>
            </button>
        </div>
    </nav>
</template>
